import _ from 'lodash';
import * as d3 from 'd3';
import { DateTime, Interval } from 'luxon';
import { LevelColoursMixin } from '../../../utility/level-colours-mixin';
import { adjustDateForDisplay } from '~/demo/date-adjustment-utils';

// we'll anchor to the first reading as long as it doesn't differ from the requested window start by more than this
const READING_INTERVAL = 4;
const MAX_DATA_GAP_HOURS = READING_INTERVAL * 6;

const noOp = () => {};

export class SmoothLevelHistoryChart extends LevelColoursMixin(class {}) {
  constructor(opts) {
    super(opts);

    this.onSelect = opts.onSelect || noOp;
    this.element = opts.element;
    this.points = opts.points;
    this.unit = opts.unit;
    this.xRange = opts.xRange;
    this.yRange = opts.yRange;
    this.yTicks = opts.yTicks;

    this.highlightWidth = opts.highlightWidth || 6;
    this.highlightSelection = opts.highlightSelection;
    this.highlightManualFeedMask = opts.highlightManualFeedMask;
    this.selectionDateRange = opts.selectionDateRange || null;

    this.hideAxes = opts.hideAxes;
    this.hideRefills = opts.hideRefills;
    this.hideLine = opts.hideLine;
    this.hideRawToggle = opts.hideRawToggle;

    this.inventoryPeriods = opts.inventoryPeriods || [];
    this.showInventoryPeriods = opts.showInventoryPeriods;
    this.selectedPeriod = null;

    /** {days | hours | hours8} */
    this.timeScale = opts.timeScale || 'days';

    // Latest bin level before date for rendering anchor
    this.latestBeforeDateLevel = opts.latestBeforeDateLevel;
    this.manualClassificationsEntityList = opts.manualClassificationsEntityList || [];

    this.svg = undefined;
    this.transitionDuration = 300;

    this.margin = {
      top: 30,
      ...this.hideAxis
        // zero all but the top margins if not displaying axes
        ? { right: 0, bottom: 0, left: 0 }
        : { right: 40, bottom: 30, left: 60 },
    };

    this.fullValue = opts.fullValue;
    this.plotInitialEstimates = opts.plotInitialEstimates || (window.localStorage.getItem('bn-feature-plotInitialEstimates') === 'true');
    this.refills = this._extractRefills();

    // don't show any forecast when we are showing a prospect refill.  The backend doesn't
    // produce a forecast on prospects so typically we would show the old forecast as if the
    // prospect is an outlier, but we have enough confidence that it's better to show the
    // refill and hide the forecast until we produce a new forecast.
    const showForecasts = _.findLast(this.points, p => !p.forecast)?.label !== 'REFILL-PROSPECT';
    if (!showForecasts) {
      this.points = this.points.filter(p => !p.forecast);
    }

    this.draw();
  }

  get closestPoint() {
    return this._closestPoint;
  }

  set closestPoint(closestPoint) {
    if (closestPoint === this._closestPoint) {
      return;
    }

    this._closestPoint = closestPoint;
    this._onClosestPointChange(closestPoint);
  }

  get closestRefill() {
    return this._closestRefill;
  }

  set closestRefill(refill) {
    if (refill === this._closestRefill) {
      return;
    }

    this._closestRefill = refill;
    this._onClosestRefillChange(refill);
  }

  get selectedPoint() {
    return this._selectedPoint;
  }

  set selectedPoint(p) {
    if (p === this._selectedPoint) {
      return;
    }

    this._selectedPoint = p;
    this._onSelectedPointChange(p);
  }

  resize() {
    const { element } = this;

    // only redraw if the element dimensions changed.  It's possible the window was resized but our host's size
    // didn't.
    if (this.lastClientHeight === element.clientHeight && this.lastClientWidth === element.clientWidth) {
      return;
    }

    d3.select(element).selectAll('*').remove();
    clearTimeout(this.resizeHandler);
    this.resizeHandler = setTimeout(() => this.draw(), 200);
  }

  draw() {
    const { element, margin } = this;
    if (!margin || !element) {
      return;
    }

    // clear any current contents
    d3.select(element).selectAll('*').remove();

    this.lastClientHeight = element.clientHeight;
    this.lastClientWidth = element.clientWidth;

    // figure out the graph dimensions
    this.height = element.clientHeight - margin.top - margin.bottom;
    this.width = element.clientWidth - margin.left - margin.right;

    if (this.height < 10 || this.width < 10) {
      return;
    }

    // add tooltip box
    d3.select(element).append('div')
      .attr('id', 'tooltip')
      .attr('class', 'tooltip');

    d3.select(element).append('div')
      .attr('id', 'tooltip2')
      .attr('class', 'tooltip');

    // add inventory periods tooltip
    d3.select(element).append('div')
      .attr('id', 'inventory-tooltip')
      .attr('class', 'inventory-tooltip');

    this.svg = d3.select(element)
      .append('svg')
      .attr('height', this.height + margin.top + margin.bottom)
      .attr('width', this.width + margin.left + margin.right)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    this.svg
      .append('rect')
      .attr('class', 'background')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.width);

    const defs = this.svg.append('defs');

    defs.append('g')
      .attr('id', 'refill-tick')
      .append('path')
      .attr('d', 'M -4,8 l 4,-8 l 4,8')
      // because <use> leverages shadow dom, it's not as easy to move this to CSS
      .attr('stroke', 'var(--gray-60)')
      .attr('stroke-width', '2px')
      .attr('fill', 'none');

    this._buildScale();

    // prevents the data rendering from going outside the axis (due to stroke widths etc)
    defs.append('clipPath')
      .attr('id', 'data-region-clip')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.width);

    this._drawAxes();
    this._drawInventoryPeriods();
    this._drawData();
    this._drawDustStatusLine();
    this._drawOverlay();
    this._drawRefills();
    this._drawStatus();
    this._drawLabels();
    this._updatePeriodSelection();
  }

  _buildScale() {
    const dates = this.points.map((d) => d.date.toJSDate());

    const start = DateTime.fromISO(this.xRange.start);
    const end = DateTime.fromISO(this.xRange.end);

    dates.push(start.toJSDate());
    dates.push(end.toJSDate());

    const [min, max] = [start.toJSDate(), end.toJSDate()];

    this.xScale = d3.scaleTime()
      .range([0, this.width])
      .domain([min, max]);

    const top = Math.max(this.yRange.end, ...this.points.map((d) => d.value));
    const bottom = this.yRange.start || 0;

    // For when a bin is giving 110% effort
    this.percentAdjustment = top / this.fullValue;

    this.yScale = d3.scaleLinear()
      .domain([bottom, top])
      .range([this.height, 0]);

    this.yPercentScale = d3.scaleLinear()
      .domain([bottom / this.fullValue, top / this.fullValue])
      .range([this.height, 0]);
  }

  _drawLabels() {
    const now = DateTime.now();
    const startTime = DateTime.fromISO(this.xRange.start);
    const endTime = DateTime.fromISO(this.xRange.end);

    if (now >= startTime && now <= endTime) {
      const xNow = this.xScale(now.toJSDate());
      const today = this.svg.append('g').attr('class', 'label now');
      today.append('line')
        .attr('x1', xNow)
        .attr('y1', -10)
        .attr('x2', xNow)
        .attr('y2', this.height + 5);

      today.append('text')
        .attr('x', xNow)
        .attr('y', -13)
        .style('text-anchor', 'middle')
        .text('Now');
    }
  }

  _drawAxes() {
    if (this.hideAxes) {
      return;
    }

    const xAxis = d3.axisBottom(this.xScale)
      .ticks(this.timeScale === 'hours' ? d3.timeHour : this.timeScale === 'hours8' ? d3.timeHour.every(8) : d3.timeDay)
      .tickFormat((d) => {
        // Use adjusted date for display in demo mode to show recent times
        const adjustedDate = adjustDateForDisplay(DateTime.fromJSDate(d)).toJSDate();
        return d3.timeFormat(this.timeScale === 'hours' ? '%H:00' : this.timeScale === 'hours8' ? '%H:00' : '%d')(adjustedDate);
      })
      .tickSizeOuter(0);

    const xAxis2 = d3.axisBottom(this.xScale)
      .ticks(d3.timeDay)
      .tickFormat((d) => {
        const domainStart = this.xScale.domain()[0];
        const isFirstTick = Math.abs(d.getTime() - domainStart.getTime()) < 24 * 60 * 60 * 1000; // within 1 day

        // Use adjusted date for display in demo mode to show recent times
        const adjustedDate = adjustDateForDisplay(DateTime.fromJSDate(d)).toJSDate();

        if (this.timeScale === 'hours' || this.timeScale === 'hours8') {
          const day = d3.timeDay(adjustedDate);
          const isFirstTickOfDay = day.getTime() === adjustedDate.getTime();
          return d3.timeFormat((isFirstTick || isFirstTickOfDay) ? '%b %d' : '')(adjustedDate);
        }

        const monthStart = d3.timeMonth(adjustedDate);
        const isFirstDayOfMonth = monthStart.getTime() === adjustedDate.getTime();
        return d3.timeFormat((isFirstTick || isFirstDayOfMonth) ? '%b' : '')(adjustedDate);
      });

    const yAxis = d3.axisLeft(this.yScale)
      .ticks(this.yTicks || 5, 's')
      .tickSizeOuter(0);

    const percentAxis = d3.axisRight(this.yPercentScale)
      .tickFormat(d3.format('~%'))
      .tickSizeInner(-this.width + 5);

    const isZoomed = this.yRange.start > 0 || this.yRange.end < this.fullTotal;
    if (isZoomed) {
      percentAxis.ticks(this.yTicks || 5);
    } else {
      percentAxis.tickValues([0, 0.25, 0.5, 0.75, 1]);
    }

    this.svg.append('g')
      .attr('class', 'y axis')
      .transition()
      .duration(this.transitionDuration)
      .call(yAxis);

    this.svg.append('g')
      .attr('class', 'y axis percent')
      .attr('transform', `translate(${this.width},0)`)
      .transition()
      .duration(this.transitionDuration)
      .call(percentAxis);

    this.svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - this.margin.left * .7)
      .attr('x', 0 - this.height / 2)
      .style('text-anchor', 'middle')
      .text(this.unit);

    this.svg.append('g')
      .attr('class', 'x axis')
      .attr('transform', () => `translate(0,${this.height})`)
      .transition()
      .duration(this.transitionDuration)
      .call(xAxis);

    this.svg.append('g')
      .attr('class', 'x axis no-ticks')
      .attr('transform', () => `translate(0,${this.height + 10})`)
      .transition()
      .duration(this.transitionDuration)
      .call(xAxis2);
  }

  // status messages, like "no data" or "loading"
  _drawStatus() {
    this.svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.width);

    this.svg.append('text')
      .attr('class', 'no-data-message')
      .attr('x', this.width / 2)
      .attr('y', this.height / 2)
      .style('text-anchor', 'middle')
      .text('No data available');

    this.svg.append('text')
      .attr('class', 'loading-message')
      .attr('x', this.width / 2)
      .attr('y', this.height / 2)
      .style('text-anchor', 'middle')
      .text('Loading data...');
  }

  _extractRefills() {
    const refills = [];

    for (let i = 0; i < this.points.length; i++) {
      const curr = this.points[i];
      if (!this._isRefill(curr)) {
        continue;
      }
      const refill = { ...curr };
      const prev = this.points[i - 1];
      if (prev) {
        refill.height = this._bestValue(curr) - this._bestValue(prev);
      }

      refill.start = prev?.label === 'PARTIAL_REFILL' ? prev.date : refill.date;
      refills.push(refill);
    }

    return refills;
  }

  _drawRefills() {
    if (this.hideRefills) {
      return;
    }
    this.refills.forEach(refill => {
      const { date } = refill;
      const x = this.xScale(date);
      const label = this.svg.append('g').attr('class', 'label');

      const margin = 6;
      const arrowHeight = 8;
      const arrowSpacing = 6;
      const start = this.yScale(this._bestValue(refill));
      const end = this.yScale(this._bestValue(refill) - refill.height);
      const height = end - start;
      const arrowCount = Math.max(1, Math.floor((height - margin * 2) / (arrowHeight + arrowSpacing)));
      const adjustedHeight = arrowCount * arrowHeight + (arrowCount - 1) * arrowSpacing;
      const adjustedMargin = (height - adjustedHeight) / 2;
      const arrowYs = [];
      for (let i = 0; i < arrowCount; i++) {
        arrowYs.push(start + adjustedMargin + i * (arrowHeight + arrowSpacing));
      }

      label
        .selectAll('refill-marker')
        .data(arrowYs)
        .enter()
        .append('use')
        .attr('xlink:href', '#refill-tick')
        .attr('x', x)
        .attr('y', d => d);
    });
  }

  _drawInventoryPeriods() {
    if (!this.showInventoryPeriods || !this.inventoryPeriods.length) {
      return;
    }

    // Draw trend areas (fill under the line)
    this.svg.selectAll('.inventory-trend-area')
      .data(this.inventoryPeriods.filter(d => d.trend !== 'uncertain'))
      .enter()
      .append('polygon')
      .attr('class', d => `inventory-trend-area ${d.trend || 'uncertain'}`)
      .attr('points', d => {
        const x1 = this._getInventoryPeriodX1(d);
        const y1 = this._getInventoryPeriodY1(d);
        const x2 = this._getInventoryPeriodX2(d);
        const y2 = this._getInventoryPeriodY2(d);
        const bottom = this.yScale(0);
        return `${x1},${y1} ${x2},${y2} ${x2},${bottom} ${x1},${bottom}`;
      })
      .attr('clip-path', 'url(#data-region-clip)');

    // Draw trend lines from start to end volume
    this.svg.selectAll('.inventory-trend-line')
      .data(this.inventoryPeriods)
      .enter()
      .append('line')
      .attr('class', d => `inventory-trend-line ${d.trend || 'uncertain'}`)
      .attr('x1', d => this._getInventoryPeriodX1(d))
      .attr('y1', d => this._getInventoryPeriodY1(d))
      .attr('x2', d => this._getInventoryPeriodX2(d))
      .attr('y2', d => this._getInventoryPeriodY2(d))
      .attr('clip-path', 'url(#data-region-clip)');

    // Draw foreground rectangles
    this.svg.selectAll('.inventory-period')
      .data(this.inventoryPeriods)
      .enter()
      .append('rect')
      .attr('class', d => `inventory-period ${d.trend || 'uncertain'}`)
      .attr('x', d => this.xScale(d.startDate))
      .attr('y', 0)
      .attr('width', d => Math.max(1, this.xScale(d.endDate) - this.xScale(d.startDate)))
      .attr('height', this.height)
      .attr('clip-path', 'url(#data-region-clip)')
      .on('mouseover', (event, d) => {
        // Show tooltip with period info
        this._showPeriodTooltip(event, d);
      })
      .on('mouseout', () => {
        this._hidePeriodTooltip();
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        this._selectPeriod(d);
      });
  }

  _showPeriodTooltip(event, period) {
    const tooltip = d3.select(this.element).select('#inventory-tooltip');
    const start = DateTime.fromISO(period.startDate);
    const end = DateTime.fromISO(period.endDate);
    const duration = end.diff(start);
    const hours = Math.floor(duration.as('hours'));
    const remainingMinutes = Math.floor(duration.as('minutes') % 60);

    // Calculate the center position of the period
    const startX = this.xScale(period.startDate);
    const endX = this.xScale(period.endDate);
    const centerX = (startX + endX) / 2 + this.margin.left; // Add margin to account for SVG offset

    const trendLabel = period.trend.charAt(0).toUpperCase() + period.trend.slice(1);

    let amount = '';
    switch (period.trend) {
      case 'consumption':
        amount = `: ${period.consumptionAmount.toFixed(1)}${this.unit}`;
        break;
      case 'stable':
        amount = `: ${period.startVolume.toFixed(1)}${this.unit} - ${period.endVolume.toFixed(1)}${this.unit}`;
        break;
      case 'refill':
        amount = `: ${(period.endVolume - period.startVolume).toFixed(1)}${this.unit}`;
        break;
      case 'uncertain':
        amount = '';
        break;
    }

    // Format duration - only show minutes if > 0
    let durationText = `${hours}h`;
    if (remainingMinutes > 0) {
      durationText += ` ${remainingMinutes}m`;
    }

    // Use adjusted dates for display in demo mode to show recent times
    const displayStart = adjustDateForDisplay(start);
    const displayEnd = adjustDateForDisplay(end);
    const dateRange = `${displayStart.toFormat('h:mm a')} - ${displayEnd.toFormat('h:mm a')}`.toLowerCase();

    const text = `${trendLabel}${amount}<br>${durationText} (${dateRange})`;

    // Set initial content to measure width
    tooltip
      .style('display', 'block')
      .html(text)
      .style('left', `${centerX}px`)
      .style('top', '30px')
      .style('transform', 'translateX(-50%)');

    // Get tooltip dimensions and container bounds
    const tooltipRect = tooltip.node().getBoundingClientRect();
    const containerRect = this.element.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width;
    const containerWidth = containerRect.width;

    // Calculate optimal position to keep tooltip in view
    let finalX = centerX;
    let transform = 'translateX(-50%)'; // Default: center on period

    // Check if tooltip would overflow on the left
    if (centerX - tooltipWidth / 2 < this.margin.left) {
      finalX = this.margin.left + tooltipWidth / 2;
      transform = 'translateX(-50%)';
    }
    // Check if tooltip would overflow on the right
    else if (centerX + tooltipWidth / 2 > containerWidth - this.margin.right) {
      finalX = containerWidth - this.margin.right - tooltipWidth / 2;
      transform = 'translateX(-50%)';
    }

    // Apply final positioning
    tooltip
      .style('left', `${finalX}px`)
      .style('transform', transform);
  }

  _hidePeriodTooltip() {
    d3.select(this.element).select('#inventory-tooltip')
      .style('display', 'none')
      .style('transform', null);
  }

  _getInventoryPeriodX1(period) {
    const rangeStart = DateTime.fromISO(this.xRange.start);
    const periodStart = period.startDate;

    // If period starts before range, use range start
    if (periodStart < rangeStart) {
      return this.xScale(rangeStart.toJSDate());
    }

    return this.xScale(periodStart.toJSDate());
  }

  _getInventoryPeriodX2(period) {
    const rangeEnd = DateTime.fromISO(this.xRange.end);
    const periodEnd = period.endDate;

    // If period ends after range, use range end
    if (periodEnd > rangeEnd) {
      return this.xScale(rangeEnd.toJSDate());
    }

    return this.xScale(periodEnd.toJSDate());
  }

  _getInventoryPeriodY1(period) {
    const rangeStart = DateTime.fromISO(this.xRange.start);
    const periodStart = period.startDate;

    // If period starts before range, interpolate between start and end volumes
    if (periodStart < rangeStart && period.startVolume != null && period.endVolume != null) {
      const totalPeriodDuration = period.endDate.diff(periodStart, 'hours').hours;
      const timeFromStartToRangeStart = rangeStart.diff(periodStart, 'hours').hours;

      // Calculate interpolation ratio (0 = start, 1 = end)
      const ratio = totalPeriodDuration > 0 ? timeFromStartToRangeStart / totalPeriodDuration : 0;

      // Linear interpolation between start and end volumes
      const volumeAtRangeStart = period.startVolume + (period.endVolume - period.startVolume) * ratio;
      return this.yScale(volumeAtRangeStart);
    }

    // Use startVolume if available, otherwise fallback to center
    return period.startVolume != null ? this.yScale(period.startVolume) : this.height / 2;
  }

  _getInventoryPeriodY2(period) {
    const rangeEnd = DateTime.fromISO(this.xRange.end);
    const periodEnd = period.endDate;

    // If period ends after range, interpolate between start and end volumes
    if (periodEnd > rangeEnd && period.startVolume != null && period.endVolume != null) {
      const totalPeriodDuration = periodEnd.diff(period.startDate, 'hours').hours;
      const timeFromStartToRangeEnd = rangeEnd.diff(period.startDate, 'hours').hours;

      // Calculate interpolation ratio (0 = start, 1 = end)
      const ratio = totalPeriodDuration > 0 ? timeFromStartToRangeEnd / totalPeriodDuration : 0;

      // Linear interpolation between start and end volumes
      const volumeAtRangeEnd = period.startVolume + (period.endVolume - period.startVolume) * ratio;
      return this.yScale(volumeAtRangeEnd);
    }

    // Use endVolume if available, otherwise fallback to center
    return period.endVolume != null ? this.yScale(period.endVolume) : this.height / 2;
  }

  _selectPeriod(period) {
    // Toggle selection: deselect if clicking the same period
    if (this.selectedPeriod === period) {
      this.selectedPeriod = null;
    } else {
      this.selectedPeriod = period;
    }

    this._updatePeriodSelection();
  }

  _getPointsInPeriod(period) {
    if (!period) {
      return [];
    }

    const startTime = period.startDate.toJSDate().getTime();
    const endTime = period.endDate.toJSDate().getTime();

    return this.points.filter(point => {
      const pointTime = point.date.toJSDate().getTime();
      return pointTime >= startTime && pointTime <= endTime && !point.forecast;
    });
  }

  _updatePeriodSelection() {
    // Update period rectangle styling
    this.svg.selectAll('.inventory-period')
      .classed('selected', d => d === this.selectedPeriod);

    // Update trend line styling
    this.svg.selectAll('.inventory-trend-line')
      .classed('selected', d => d === this.selectedPeriod);

    // Update trend area styling
    this.svg.selectAll('.inventory-trend-area')
      .classed('selected', d => d === this.selectedPeriod);

    // Remove existing overlay points
    this.svg.selectAll('.period-overlay-point').remove();

    // Add overlay points for selected period
    if (this.selectedPeriod) {
      const pointsInPeriod = this._getPointsInPeriod(this.selectedPeriod);

      this.svg.selectAll('.period-overlay-point')
        .data(pointsInPeriod)
        .enter()
        .append('circle')
        .attr('class', 'period-overlay-point')
        .attr('cx', d => this.xScale(d.date.toJSDate()))
        .attr('cy', d => this.yScale(d.value))
        .attr('r', 2)
        .attr('clip-path', 'url(#data-region-clip)');
    }
  }

  _drawData() {
    const thresholds = [...this.thresholds].sort((a, b) => a.percent - b.percent);
    const gradientStops = [{ ...thresholds[0], percent: 0 }, thresholds[0]];

    for (let i = 1; i < thresholds.length; i++) {
      gradientStops.push({ ...thresholds[i], percent: thresholds[i - 1].percent + 0.01 });
      gradientStops.push(thresholds[i]);
    }

    this.svg.append('linearGradient')
      .attr('id', 'temperature-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0).attr('y1', this.height)
      .attr('x2', 0).attr('y2', 0)
      .selectAll('stop')
      .data(gradientStops)
      .enter().append('stop')
      .attr('offset', (d) => `${d.percent / this.percentAdjustment}%`)
      .attr('stop-color', (d) => d.darkColour);

    const makeLine = property => d3.line()
      .curve(d3.curveLinear)
      .x((d) => this.xScale(d.date.toJSDate()))
      .y((d) => this.yScale(typeof d[property] !== 'undefined' ? d[property] : d.value));

    const [forecasts, readings] = _.partition(this.points, p => p.forecast);
    const readingsWithAnchors = [...readings.filter(({ label }) => label !== 'OUTLIER')];

    // Add anchor point for latest bin level before date range to ensure line renders properly from the left edge
    if (this.latestBeforeDateLevel && readingsWithAnchors.length > 0) {
      readingsWithAnchors.unshift({
        date: this.latestBeforeDateLevel.date,
        value: this.latestBeforeDateLevel.value,
        estimatedValue: this.latestBeforeDateLevel.estimatedValue,
        initialEstimatedValue: this.latestBeforeDateLevel.initialEstimatedValue,
        anchor: true,
      });
    }

    // insert fake points at refill points to make the refill look instantaneous when graphing.  These points are not
    // shown and will not be snapped to with the crosshairs
    let prevReadingWasRefill = false;
    for (let i = 1; i < readingsWithAnchors.length; i++) {
      let prevReading = readingsWithAnchors[i - 1];
      let reading = readingsWithAnchors[i];

      if (this._exceedsMaxGap(prevReading, reading)) {
        readingsWithAnchors.splice(i, 0, {
          date: prevReading.date,
          value: 0,
          estimatedValue: 0,
          initialEstimatedValue: 0,
          disjoint: true,
        },{
          date: reading.date,
          value: 0,
          estimatedValue: 0,
          initialEstimatedValue: 0,
          disjoint: true,
        });
        i += 2;
        // prev reading should now be the right gap anchor, in case it's also a refill
        prevReading = readingsWithAnchors[i - 1];
      }

      if (this._isRefill(reading)) {
        // in the case of two successive refills, we don't want to colour the line at the partial refill
        if (prevReadingWasRefill) {
          readingsWithAnchors[i - 1] = { ...readingsWithAnchors[i - 1], disjoint: true };
        }

        // these are only aids in rendering the line; they are not real data points
        readingsWithAnchors.splice(i, 0, {
          date: reading.date,
          value: prevReading.value,
          estimatedValue: prevReading.estimatedValue,
          initialEstimatedValue: prevReading.initialEstimatedValue,
        },{
          date: reading.date,
          value: reading.value,
          estimatedValue: reading.estimatedValue,
          initialEstimatedValue: reading.initialEstimatedValue,
          disjoint: true,
        });
        prevReadingWasRefill = true;
        i += 2;
      } else {
        prevReadingWasRefill = false;
      }
    }

    const lastReading = readings[readings.length - 1];
    if (lastReading && forecasts.length > 0) {
      forecasts.unshift({
        ...lastReading,
        value: this._bestValue(lastReading),
      });
    }

    // disjoint prevents us from connecting the line before or after the disjoint point.
    const pastLine = makeLine('estimatedValue').defined(d => !d.disjoint);

    const pastArea = d3.area()
      .x(pastLine.x())
      .y1(pastLine.y())
      .y0(this.yScale(0));

    const forecastLine = makeLine('value');

    const forecastArea = d3.area()
      .x(forecastLine.x())
      .y1(forecastLine.y())
      .y0(this.yScale(0));

    this.svg.append('path')
      .datum(forecasts)
      .attr('d', forecastArea)
      .attr('class', 'fill-area forecast')
      .attr('fill-opacity', '0')
      .attr('clip-path', 'url(#data-region-clip)')
      .transition()
      .ease(d3.easeExpOut)
      .duration(this.transitionDuration)
      .attr('fill-opacity', 0.3);

    this.svg.append('path')
      .datum(forecasts)
      .attr('d', forecastLine)
      .attr('class', 'fill-line forecast')
      .attr('stroke-opacity', 0)
      .attr('clip-path', 'url(#data-region-clip)')
      .transition()
      .ease(d3.easeExpOut)
      .duration(this.transitionDuration)
      .attr('stroke-opacity', 1);

    if (!this.hideLine) {
      this.svg.append('path')
        .datum(readingsWithAnchors)
        .attr('d', pastLine)
        .attr('class', 'fill-line past-estimates')
        .attr('stroke', 'url(#temperature-gradient)')
        .attr('stroke-opacity', 0)
        .attr('clip-path', 'url(#data-region-clip)')
        .transition()
        .ease(d3.easeExpOut)
        .duration(this.transitionDuration)
        .attr('stroke-opacity', 1);
    }

    if (this.plotInitialEstimates) {
      this.svg.append('path')
        .datum(readings)
        .attr('d', makeLine('initialEstimatedValue'))
        .attr('class', 'fill-line initial-estimates')
        .attr('stroke-opacity', 0)
        .attr('clip-path', 'url(#data-region-clip)')
        .transition()
        .ease(d3.easeExpOut)
        .duration(this.transitionDuration)
        .attr('stroke-opacity', 0.3);

      this.svg.append('path')
        .datum(readingsWithAnchors)
        .attr('d', pastArea)
        .attr('class', 'fill-area past-estimates')
        .attr('fill-opacity', '0')
        .attr('clip-path', 'url(#data-region-clip)')
        .transition()
        .ease(d3.easeExpOut)
        .duration(this.transitionDuration)
        .attr('fill-opacity', 0.3);
    }

    this.svg.selectAll('rawPoints')
      .data(readings.filter(d => !d.anchor))
      .enter()
      .append('circle')
      .attr('class', 'raw-point')
      .classed('outlier', ({ label }) => label === 'OUTLIER')
      .classed('prospect', ({ label }) => label === 'REFILL-PROSPECT')
      .classed('manual', ({ source }) => source === 'manual')
      .attr('fill', d => this._getThresholdColour(d, this.hideLine ? 'colour' : 'darkerColour'))
      .attr('cx', d => this.xScale(d.date.toJSDate()))
      .attr('cy', (d) => this.yScale(d.value))
      .attr('r', ({ source }) => source === 'manual' ? 3 : 2);

    // show manual feed mask marks
    if (this.highlightManualFeedMask) {
      // draw a line at the bottom of every point with a feedMask
      this.svg.selectAll('feedMaskMarks')
        .data(readings.filter(r => r.feedMask && !r.anchor))
        .enter()
        .append('rect')
        .attr('x', d => this.xScale(d.date.toJSDate()) - (this.highlightWidth / 2))
        .attr('y', this.height - 5)
        .attr('height', 3)
        .attr('width', this.highlightWidth)
        .attr('class', 'manual-feed-mask');
    }
  }

  _drawOverlay() {
    // show what's currently selected
    this.selection = this.svg
      .append('g')
      .style('display', 'none')
      .attr('class', 'selection');
    this.selection
      .append('circle')
      .attr('r', 5)
      .attr('id', 'selectionCircle')
      .attr('class', 'selectionCircle');
    this.selection
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.height)
      .attr('width', this.highlightWidth)
      .attr('id', 'selectionRect')
      .attr('class', 'selectionRect');

    // show where mouse is hovering
    this.focus = this.svg
      .append('g')
      .style('display', 'none')
      .attr('class', 'focus');
    this.focus
      .append('line')
      .attr('id', 'focusLineX')
      .attr('class', 'focusLine');
    this.focus
      .append('line')
      .attr('id', 'focusLineY')
      .attr('class', 'focusLine');
    this.focus
      .append('circle')
      .attr('r', 5)
      .attr('id', 'focusCircle')
      .attr('class', 'focusCircle');

    this.svg
      .on('click', (event) => {
        // Only clear period selection if clicking on background (not on a period)
        if (event.target.classList.contains('background')) {
          this.selectedPeriod = null;
          this._updatePeriodSelection();
        }
        this._selectPoint(this.closestPoint);
      })
      .on('mouseover', () => this.focus.style('display', null))
      .on('mousemove', event => {
        const d3Mouse = d3.pointer(event);
        const date = this.xScale.invert(d3Mouse[0]);
        this.closestPoint = this._findClosestPoint(date);
      })
      .on('mouseout', () => {
        this.focus.style('display', 'none');
        this.closestPoint = null;
      });
  }

  _findClosestPoint(date) {
    const luxonDate = DateTime.fromJSDate(date);
    const bisect = d3.bisector((d) => d.date.toJSDate()).left;
    const i = bisect(this.points, date);
    // get closest point
    const p1 = this.points[i - 1];
    const p2 = this.points[i];
    if (p1 && p2) {
      const p1DateTime = p1.date;
      const p2DateTime = p2.date;
      return luxonDate.diff(p1DateTime) > p2DateTime.diff(luxonDate) ? p2 : p1;
    }
    return p1 || p2;
  }

  _onClosestPointChange(closestPoint) {
    if (!this.closestPoint) {
      this._tip();
      this.closestRefill = null;
      return;
    }

    const x = this.xScale(closestPoint.date.toJSDate());
    const y = this.yScale(this._bestValue(closestPoint));
    // draw circle
    this.focus.select('#focusCircle')
      .attr('cx', x)
      .attr('cy', y);
    // draw lines
    this.focus.select('#focusLineX')
      .attr('x1', x).attr('y1', 0)
      .attr('x2', x).attr('y2', this.height);
    this.focus.select('#focusLineY')
      .attr('x1', 0).attr('y1', y)
      .attr('x2', this.width).attr('y2', y);
    this._tip(this._tipFormat(closestPoint));

    this.closestRefill = this.refills.find(({ label, start }, i) => {
      const next = this.refills[i + 1];
      const { date } = closestPoint;
      return start <= date && label === 'REFILL' && (!next?.date || (date < next.date));
    });
  }

  _onClosestRefillChange(refill) {
    if (refill?.date && !this.hideRefills) {
      const luxonDate = refill.date;
      const date = luxonDate.toFormat('MMM d, h a');
      this._tip2(`Refilled on ${date}`);
    } else {
      this._tip2();
    }
  }

  _onSelectedPointChange(p) {
    // If you change tabs immediately after making a selection this can be called without a selection
    if (!this.selection) {
      return;
    }
    this.selection.style('display', (p && this.highlightSelection) ? null : 'none');

    if (!p) {
      return;
    }
    const x = this.xScale(p.date.toJSDate());
    const y = this.yScale(this._bestValue(p));
    this.selection.select('#selectionCircle')
      .attr('cx', x)
      .attr('cy', y);

    this.selection.select('#selectionRect')
      .attr('x', x - (this.highlightWidth / 2));
  }

  _selectPoint(p) {
    this.selectedPoint = p;
    this.onSelect(p);
  }

  selectByDate(date) {
    this._selectPoint(this._findClosestPoint(date));
  }

  selectPrevious() {
    if (!this.selectedPoint) {
      this._selectPoint(this.points[this.points.length - 1]);
    }
    const i = this.points.indexOf(this.selectedPoint);
    if (i < 1) {
      return;
    }
    this._selectPoint(this.points[i - 1]);
  }

  selectNext() {
    if (!this.selectedPoint) {
      this._selectPoint(this.points[0]);
    }
    const i = this.points.indexOf(this.selectedPoint);
    if (i === -1 || i === this.points.length - 1) {
      return;
    }
    this._selectPoint(this.points[i + 1]);
  }

  _getThresholdColour(d, variant = 'colour') {
    if (d.source === 'manual') {
      return 'var(--gray-60)';
    }
    if (this.showInventoryPeriods) {
      return 'var(--teal-20)';
    }
    return this.getLevelColour(100 * d.value / this.fullValue, variant);
  }

  _updateTip(id, html) {
    const el = d3.select(this.element).select(id);
    if (!html) {
      el.html('').style('display', 'none');
    } else {
      el.html(html).style('display', null);
    }
  }

  _tip(text) {
    this._updateTip('#tooltip', text);
  }

  _tip2(text) {
    this._updateTip('#tooltip2', text);
  }

  _tipFormat(d) {
    const luxonDate = d.date;
    // Use adjusted date for display in demo mode to show recent times
    const displayDate = adjustDateForDisplay(luxonDate);
    const date = this.timeScale === 'hours' || this.timeScale === 'hours8'
      ? displayDate.toFormat('MMM d, HH:mm')
      : displayDate.toFormat('MMM d, h:mm a');
    let context;
    if (d.forecast) {
      context = 'Forecast';
    } else if (d.source === 'manual') {
      context = 'Manual Inventory';
    }
    return `${`${date} - <span class="magnitude">${this._bestValue(d)}</span> ${this.unit}` +
      `${context ? ` [${context}]` : ''}`}${
      !d.forecast ? `<span class="raw">Raw reading: ${d.value} ${this.unit}</span>` : ''}`;
  }

  _bestValue(point) {
    if (!point) {
      return;
    }
    if (typeof point.estimatedValue !== 'undefined') {
      return point.estimatedValue;
    }

    return point.value;
  }

  _exceedsMaxGap(first, second) {
    if (!first || !second) {
      return false;
    }

    const firstDate = first.date;
    const secondDate = second.date;
    return secondDate.diff(firstDate, 'hours').hours > MAX_DATA_GAP_HOURS;
  }

  _isRefill(point) {
    return ['REFILL', 'PARTIAL_REFILL', 'REFILL-PROSPECT'].includes(point?.label);
  }

  _isBetween(start, end, target) {
    const date = DateTime.fromISO(target);
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);
    // Luxon intervals are closed at the end
    return Interval.fromDateTimes(startDate, endDate).contains(date) || endDate.equals(date);
  }

  _isInSelectionRange(point) {
    return this.selectionDateRange &&
      this._isBetween(this.selectionDateRange.startPublishedDate, this.selectionDateRange.endPublishedDate, point?.date);
  }

  _getDustPointColor(point, isDusty, isManual) {
    if (this._isInSelectionRange(point)) {
      return 'var(--yellow-60, #ffc107)';
    }
    if (!isManual) {
      return null;
    }
    return isDusty ? 'var(--gray-60)' : 'var(--green-60, #4caf50)';
  }

  _checkManualDustClassification(date) {
    const matchingRange = this.manualClassificationsEntityList.map((entity) => entity.properties)
      .filter(props => props.classificationKey === 'dust')
      .find(props => this._isBetween(props.startDate, props.endDate, date));
    if (matchingRange) {
      return { isManual: true, isDusty: matchingRange.classificationValue };
    }

    return { isManual: false, isDusty: false };
  }

  _drawStatusLineSegment(dustStatusLine, x1, x2, y, color) {
    dustStatusLine.append('line')
      .attr('x1', x1)
      .attr('y1', y)
      .attr('x2', x2)
      .attr('y2', y)
      .attr('stroke-linecap', 'square')
      .attr('stroke', color)
      .attr('stroke-width', 2);
  }

  _drawDustStatusLine() {
    if (!this.highlightManualFeedMask) {
      return;
    }

    if (this.points.length < 2) {
      return;
    }

    const dustStatusLine = this.svg.append('g');

    const y = 0; // Position at very top of graph

    for (let i = 0; i < this.points.length - 1; i++) {
      const currentPoint = this.points[i];
      const prevPoint = this.points[Math.max(0, i - 1)];
      const nextPoint = this.points[i + 1];

      const x1 = this.xScale(currentPoint.date);
      const x2 = this.xScale(nextPoint.date);

      const { isManual, isDusty } = this._checkManualDustClassification(currentPoint.date);

      const currentColor = this._getDustPointColor(currentPoint, isDusty, isManual);
      if (currentColor === null) {
        // No line for things that haven't been classified
        continue;
      }
      const { isManual: prevManual, isDusty: prevDusty } = this._checkManualDustClassification(prevPoint.date);
      const prevColor = this._getDustPointColor(prevPoint, prevDusty, prevManual);
      const { isManual: nextManual, isDusty: nextDusty } = this._checkManualDustClassification(nextPoint.date);
      const nextColor = this._getDustPointColor(nextPoint, nextDusty, nextManual);

      const leftTransition = currentColor !== prevColor;
      const rightTransition = currentColor !== nextColor;

      if (leftTransition && rightTransition) {
        // Draw a small segment for single items
        this._drawStatusLineSegment(dustStatusLine, x1 - 3, x1 + 3, y, currentColor);
        continue;
      }

      // Skip the last part of the line, otherwise it extends over the next point
      if (rightTransition) {
        continue;
      }

      this._drawStatusLineSegment(dustStatusLine, x1, x2, y, currentColor);
    }
  }
}
